import { gqlClient } from '../../components/AppProvider';
import { AppDispatch, AppThunk } from '../../store';
import { RequestResult, DataLakeDirectoryListPayload, DataLakeFileDownloadRequestInput, BlobDocumentFormat, DataLakeFileUploadRequestInput, DataLakeDirectoryCreateRequestInput } from '../../gql-types.generated';
import { queryFileDirectories } from '../../gql/QueryFileDirectories';
import { getErrorMessage } from '../../util/Common';
import { 
    captureDeleteFileStatus, 
    captureDownloadFileStatus, 
    captureUploadFileStatus, 
    fetchDirectoryDetailsSuccess, 
    fetchError, 
    fetchFileDirectoriesSuccess, 
    fetchFileSearchFilesSuccess,
    setSearchLoading,
    clearSearchLoading,
    fetchActionError,
    captureMoveFileStatus,
    captureReprocessFileStatus,
    captureCreateDirectoryStatus,
    captureDeleteDirectoryStatus,
    fetchDirectoryHierarchySuccess
} from './FileManagerSlice';
import { queryFileDirectoryByPath } from '../../gql/QueryFileDirectoryByPath';
import { MutationDownloadDataLakeFile } from '../../gql/MutationExecuteDataLakeFileDownload';
import { MutationUploadDataLakeFile } from '../../gql/MutationExecuteDataLakeFileUpload';
import { mutationDeleteDataLakeFile } from '../../gql/MutationDeleteDataLakeFile';
import { queryFileDirectoryFiles } from '../../gql/QueryFileDirectoryFiles';
import { mutationMoveDataLakeFile } from '../../gql/MutationMoveDataLakeFile';
import { mutationReprocessDataLakeFile } from '../../gql/MutationReprocessFailedDataLakeFile';
import { MutationCreateDataLakeDirectory } from '../../gql/MutationCreateDataLakeDirectory';
import { MutationDeleteDataLakeDirectory } from '../../gql/MutationDeleteDataLakeDirectory';

export const fetchFileDirectories = (directoryPath?: string) => 
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const fetchStatus = await queryFileDirectories(gqlClient, directoryPath);
            if (fetchStatus) {
                // console.log('fetchFileDirectories response:', {
                //     result: fetchStatus.result,
                //     hasJson: !!fetchStatus.directoryHierarchyJson,
                //     message: fetchStatus.message,
                //     errors: fetchStatus.errors
                // });

                if (fetchStatus.result === RequestResult.Fail) {
                    getErrorMessage(fetchStatus.message as string, fetchStatus.errors, true, true);
                    return;
                }

                if (!fetchStatus.directoryHierarchyJson) {
                    dispatch(fetchError(new Error('No directory data received')));
                    return;
                }
                if (directoryPath !== undefined && directoryPath !== '') {
                    // If a specific directory path was requested, fetch the directory hierarchy status
                    dispatch(fetchDirectoryHierarchySuccess(fetchStatus as DataLakeDirectoryListPayload));
                } else {
                    dispatch(fetchFileDirectoriesSuccess(fetchStatus as DataLakeDirectoryListPayload));
                }
            }
        } catch (e) {
            console.error('Error fetching directories:', e);
            dispatch(fetchError(e as Error));
        }
    };

export const fetchDirectoryByPath = (directoryPath: string) =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const directory = await queryFileDirectoryByPath(gqlClient, directoryPath);
            if (directory) {
                dispatch(fetchDirectoryDetailsSuccess(directory));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        }
    };

export const fetchFileSearchResults = (fileNameSearch: string) =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            dispatch(setSearchLoading());
            const files = await queryFileDirectoryFiles(gqlClient, fileNameSearch);
            if (files) {
                dispatch(fetchFileSearchFilesSuccess(files));
            }
        } catch (e) {
            dispatch(fetchError(e as Error));
        } finally {
            dispatch(clearSearchLoading());
        }
    };

export const downloadFile = (path: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const fileDownloadData: DataLakeFileDownloadRequestInput = {
                fileNameWithFullPath: path,
                blobDocumentFormat: BlobDocumentFormat.Base64,
            };
            const downloadStatus = await MutationDownloadDataLakeFile(gqlClient, fileDownloadData);
            
            if (downloadStatus) {
                if (downloadStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(downloadStatus.message as string, downloadStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureDownloadFileStatus(downloadStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const uploadFile = (files: Array<{content: string, fileName: string}>): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            // Convert files array to the required DataLakeFileUploadRequestInput format
            const bulkFileUploadData: DataLakeFileUploadRequestInput[] = files.map(file => ({
                fileContent: file.content,
                fileNameWithFullPath: file.fileName,
                blobDocumentFormat: BlobDocumentFormat.Base64,
            }));
            
            const uploadStatus = await MutationUploadDataLakeFile(gqlClient, bulkFileUploadData);
            
            if (uploadStatus) {
                if (uploadStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(uploadStatus.message as string, uploadStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureUploadFileStatus(uploadStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const createDirectory = (directoryName: string, parentDirectoryPath: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const directoryData: DataLakeDirectoryCreateRequestInput = {
                directoryName,
                parentDirectoryPath
            };
            const createStatus = await MutationCreateDataLakeDirectory(gqlClient, directoryData);
            
            if (createStatus) {
                if (createStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(createStatus.message as string, createStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureCreateDirectoryStatus(createStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const deleteDirectory = (directoryFullPath: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const deleteStatus = await MutationDeleteDataLakeDirectory(gqlClient, directoryFullPath);
            if (deleteStatus) {
                if (deleteStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(deleteStatus.message as string, deleteStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureDeleteDirectoryStatus(deleteStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const deleteFile = (fileNamesWithFullPaths: string[]): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const deleteStatus = await mutationDeleteDataLakeFile(gqlClient, fileNamesWithFullPaths);
            if (deleteStatus) {
                if (deleteStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(deleteStatus.message as string, deleteStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureDeleteFileStatus(deleteStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const moveFile = (fileNamesWithFullPaths: string[], newDestinationPath: string): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const moveStatus = await mutationMoveDataLakeFile(gqlClient, fileNamesWithFullPaths, newDestinationPath);
            if (moveStatus) {
                if (moveStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(moveStatus.message as string, moveStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureMoveFileStatus(moveStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };

export const reprocessFile = (fileNamesWithFullPaths: string[]): AppThunk =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const reprocessStatus = await mutationReprocessDataLakeFile(gqlClient, fileNamesWithFullPaths);
            if (reprocessStatus) {
                if (reprocessStatus.result === RequestResult.Fail) {
                    let errMessage = getErrorMessage(reprocessStatus.message as string, reprocessStatus.errors, true, true);
                    dispatch(fetchActionError({ message: errMessage } as Error));
                }
                dispatch(captureReprocessFileStatus(reprocessStatus));
            }
        } catch (e) {
            dispatch(fetchActionError(e as Error));
        }
    };
